import { Flex, Spinner, Text, VStack } from "@chakra-ui/react";
import { matchPath, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useAppDispatch, useAppSelector } from "app/hooks";
import ButtonComponent from "components/form/Button";
import NoPermission from "components/NoPermission";
import { getAccountDetails } from "features/main/profile/slice";
import { ROUTE_ACCESS, hasRouteAccess } from "utils/permissions";

const RequirePermission = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { accountDetails, accountStatus } = useAppSelector(
    (state) => state.profile
  );

  const handleRetry = () => {
    const onFailure = (errorResponse: IErrorResponse) => {
      toast.error(errorResponse?.message ?? "Could not load your permissions.");
    };

    dispatch(getAccountDetails({ onFailure }));
  };

  const access = Object.entries(ROUTE_ACCESS).find(([pattern]) =>
    matchPath(pattern, pathname)
  )?.[1];

  if (!access) return <>{children}</>;

  if (accountStatus === "idle" || accountStatus === "loading") {
    return (
      <Flex h="60vh" w="100%" align="center" justify="center">
        <Spinner size="lg" color="primary" thickness="3px" />
      </Flex>
    );
  }

  if (accountStatus === "error") {
    return (
      <Flex h="60vh" w="100%" align="center" justify="center">
        <VStack spacing={4} textAlign="center">
          <Text fontSize="20px" fontWeight={700}>
            We couldn&apos;t load your permissions.
          </Text>
          <Text color="textMuted">
            This section stays locked until we can confirm your access.
          </Text>
          <ButtonComponent
            width="auto"
            label="Try again"
            size="lg"
            variant="solid"
            onClick={handleRetry}
          />
        </VStack>
      </Flex>
    );
  }

  return hasRouteAccess(accountDetails, access) ? (
    <>{children}</>
  ) : (
    <NoPermission />
  );
};

export default RequirePermission;
